import { Inject, Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, timer, forkJoin, throwError, merge, EMPTY } from 'rxjs';
import { map, catchError, switchMap, retry, tap, delay, distinctUntilChanged, filter, mergeMap } from 'rxjs/operators';
import { VerifyContactService } from '../../services/verify-contact.service';

// ⏱️ TEMPORARY: Set this to a positive value (e.g. 3000) to simulate service delay and test loader animations.
// Default is 0 (no delay). Remove before committing to production.
const SIMULATED_DELAY_MS = 0;
import * as PolicyActions from './policy.actions';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { getAuthToken } from 'src/app/auth.interceptor';
import { SelectionService } from 'src/app/services/selection.service';
import { SessionService } from 'src/app/services/session.service';
import { AppConfig, APP_CONFIG } from 'src/app.config';
import { globalMessages } from 'src/app/shared/global-messages';

@Injectable()
export class PolicyEffects {
    private actions$ = inject(Actions);
    private httpClient = inject(HttpClient);
    private selectionService = inject(SelectionService);
    private sessionService = inject(SessionService);

    private verifyContactService = inject(VerifyContactService);

    constructor(@Inject(APP_CONFIG) private config: AppConfig) {}

    /**
     * Builds the Policy Orchestrator headers. For third-party sessions the
     * `sessionId` is attached as a request header (the Orchestrator authenticates
     * TP requests from this header). When no TP session is stored, only the API
     * key is sent, preserving the CSR (Okta) flow unchanged.
     */
    private orchestratorHeaders(): HttpHeaders {
        let headers = new HttpHeaders({ 'x-api-key': this.config.policyOrchestratorApiKey });
        const sessionId = this.sessionService.getSessionId();
        if (sessionId) {
            headers = headers.set('sessionId', sessionId);
        }
        return headers;
    }

    loadPolicyDetails$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PolicyActions.loadPolicyDetails),
            switchMap(({ policyNumber, policyTerm }) => {
                this.selectionService.setSelectedItem(policyNumber);
                const body = {
                    authorizationToken: `Bearer ${getAuthToken()}`,
                    policyNumber: policyNumber,
                    requestType: 'getPolicySnapshotForCSR',
                    userAuthenticated: true,
                    ...(policyTerm && { termNo: policyTerm }),
                };
                return this.httpClient
                    .post(this.config.policyOrchestratorUrl, body, {
                        headers: this.orchestratorHeaders(),
                    })
                    .pipe(
                        retry({
                            count: 2,
                            delay: (error) => {
                                if (error instanceof HttpErrorResponse && error.status >= 502) {
                                    return timer(2000);
                                }
                                throw error;
                            },
                        }),
                        map((data) => {
                            return PolicyActions.loadPolicyDetailsSuccess({ data });
                        }),
                        catchError((error) => of(PolicyActions.loadPolicyDetailsFailure({ error: error.message })))
                    );
            })
        )
    );

    loadPolicyTermDetail$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PolicyActions.loadPolicyTermDetail),
            switchMap(({ payload, policyTerm }) => {
                return this.httpClient
                    .post(this.config.policyOrchestratorUrl, payload, {
                        headers: this.orchestratorHeaders(),
                    })
                    .pipe(
                        map((data) => PolicyActions.loadPolicyTermDetailSuccess({ data })),
                        catchError((error) =>
                            of(
                                PolicyActions.loadPolicyTermDetailFailure({ error: error.message }),
                                PolicyActions.resetPolicyState()
                            )
                        )
                    );
            })
        )
    );

    private formatEffDt(date: string): string {
        const parts = date.split('-');
        if (parts.length === 3) return `${parts[0]}-${parts[1]}-${parts[2]}`;
        return date;
    }

    // Helper methods for HTTP calls with retry logic
    private fetchDCPolicy(policyNumber: string, effectiveDate?: string, expireDate?: string) {
        const effDt = effectiveDate && effectiveDate !== 'unknown' ? this.formatEffDt(effectiveDate) : undefined;
        const expDt = expireDate && expireDate !== 'unknown' ? this.formatEffDt(expireDate) : undefined;

        // Third-party flow: route through the Policy Orchestrator using the
        // getPolicyForTP request type with the sessionId header (no direct
        // service call). Detected by the presence of a stored TP session.
        const policySessionId = this.sessionService.getSessionId();
        if (policySessionId) {
            const tpBody = {
                requestType: 'getPolicyForTP',
                sessionId: policySessionId,
                policyNumber,
                ...(effDt && { effDt }),
                ...(expDt && { expDt }),
            };
            return this.httpClient
                .post(this.config.policyOrchestratorUrl, tpBody, { headers: this.orchestratorHeaders() })
                .pipe(
                    retry({
                        count: 2,
                        delay: (error) =>
                            error instanceof HttpErrorResponse && error.status >= 502
                                ? timer(2000)
                                : throwError(() => error),
                    }),
                    catchError((error) => of({ _error: true, message: error.message }))
                );
        }

        const body = {
            pathParameters: {
                serviceCategory: globalMessages.policyCategory,
                requestType: globalMessages.policyCategory,
                policyNumber,
            },
            queryStringParameters: {
                application: globalMessages.application360,
                carrier: globalMessages.psicCarrier,
                ...(expDt && { expDt }),
            },
            requestContext: {
                requestId: `req-${Math.random().toString(36).substring(2, 11)}`,
            },
        };
        return this.httpClient.post(this.config.getPolicyDataURL, body).pipe(
            retry({
                count: 2,
                delay: (error) =>
                    error instanceof HttpErrorResponse && error.status >= 502 ? timer(2000) : throwError(() => error),
            }),
            catchError((error) => of({ _error: true, message: error.message }))
        );
    }

    private fetchPayPlan(policyNumber: string, effDt: string, expDt: string) {
        // Third-party flow: getPayPlanForTP via the Orchestrator.
        // Pass effDt/expDt so the orchestrator uses the user-selected term
        // instead of falling back to the latest term from PhoneSearch.
        const payPlanSessionId = this.sessionService.getSessionId();
        if (payPlanSessionId) {
            const tpBody = {
                requestType: 'getPayPlanForTP',
                sessionId: payPlanSessionId,
                policyNumber,
                ...(effDt ? { effDt } : {}),
                ...(expDt ? { expDt } : {}),
            };
            return this.httpClient
                .post(this.config.policyOrchestratorUrl, tpBody, { headers: this.orchestratorHeaders() })
                .pipe(
                    retry({
                        count: 2,
                        delay: (error) =>
                            error instanceof HttpErrorResponse && error.status >= 502
                                ? timer(2000)
                                : throwError(() => error),
                    }),
                    catchError((error) => of({ _error: true, message: error.message }))
                );
        }

        const body = {
            pathParameters: {
                serviceCategory: globalMessages.billingCategory,
                requestType: 'payplan',
                policyNumber,
            },
            queryStringParameters: {
                application: globalMessages.application360,
                carrier: globalMessages.psicCarrier,
                effDt,
                expDt,
            },
            requestContext: {
                requestId: `req-${Math.random().toString(36).substring(2, 11)}`,
            },
        };
        return this.httpClient.post(this.config.getPolicyDataURL, body).pipe(
            retry({
                count: 2,
                delay: (error) =>
                    error instanceof HttpErrorResponse && error.status >= 502 ? timer(2000) : throwError(() => error),
            }),
            catchError((error) => of({ _error: true, message: error.message }))
        );
    }

    private fetchDCBilling(policyNumber: string, effectiveDate?: string) {
        const effDt =
            effectiveDate && effectiveDate !== 'unknown' ? this.formatEffDt(effectiveDate) : undefined;

        // Third-party flow: getBillingForTP via the Orchestrator ({ policyNumber, effDt }).
        const billingSessionId = this.sessionService.getSessionId();
        if (billingSessionId) {
            const tpBody = {
                requestType: 'getBillingForTP',
                sessionId: billingSessionId,
                policyNumber,
                ...(effDt && { effDt }),
            };
            return this.httpClient
                .post(this.config.policyOrchestratorUrl, tpBody, { headers: this.orchestratorHeaders() })
                .pipe(
                    retry({
                        count: 2,
                        delay: (error) =>
                            error instanceof HttpErrorResponse && error.status >= 502
                                ? timer(2000)
                                : throwError(() => error),
                    }),
                    catchError((error) => of({ _error: true, message: error.message }))
                );
        }

        const body = {
            pathParameters: {
                serviceCategory: globalMessages.billingCategory,
                requestType: globalMessages.billingCategory,
                policyNumber,
            },
            queryStringParameters: {
                application: globalMessages.application360,
                carrier: globalMessages.psicCarrier,
            },
            requestContext: {
                requestId: `req-${Math.random().toString(36).substring(2, 11)}`,
            },
        };
        return this.httpClient.post(this.config.getPolicyDataURL, body).pipe(
            retry({
                count: 2,
                delay: (error) =>
                    error instanceof HttpErrorResponse && error.status >= 502 ? timer(2000) : throwError(() => error),
            }),
            catchError((error) => of({ _error: true, message: error.message }))
        );
    }

    private fetchComm(policyNumber: string, policyTerm?: string, effectiveDate?: string) {
        // Inbound/outbound communications is a CSR-only feature served by IRIS and
        // authenticated with a Bearer token. Third-party sessions have no such
        // token and the TP view does not render comms, so skip the IRIS call
        // entirely and return an inert result to preserve the forkJoin shape.
        if (this.sessionService.getSessionId()) {
            return of([]);
        }

        const headers = new HttpHeaders({
            'x-api-key': this.config.irisApiKey,
            'Content-Type': 'application/json',
        });
        const body = {
            authorizationToken: `Bearer ${getAuthToken()}`,
            policyNo: policyNumber,
            term: policyTerm ? policyTerm.padStart(2, '0') : policyTerm,
            ...(effectiveDate && { effectiveDate }),
        };
        return this.httpClient
            .post(this.config.irisApiURL + globalMessages.fetchInboundCommsServiveURL, body, { headers })
            .pipe(
                retry({
                    count: 2,
                    delay: (error) =>
                        error instanceof HttpErrorResponse && error.status >= 502
                            ? timer(2000)
                            : throwError(() => error),
                }),
                catchError((error) => of({ _error: true, message: error.message }))
            );
    }

    private fetchDMS(policyNumber: string, policyTerm?: string, effectiveDate?: string) {
        // Third-party flow: getDocumentForTP via the Orchestrator ({ policyNumber, effDt });
        // the term is derived server-side. No Bearer token; auth is the sessionId header.
        const dmsSessionId = this.sessionService.getSessionId();
        if (dmsSessionId) {
            const effDt =
                effectiveDate && effectiveDate !== 'unknown' ? this.formatEffDt(effectiveDate) : undefined;
            const tpBody = {
                requestType: 'getDocumentForTP',
                sessionId: dmsSessionId,
                policyNumber,
                ...(effDt && { effDt }),
            };
            return this.httpClient
                .post(this.config.policyOrchestratorUrl, tpBody, { headers: this.orchestratorHeaders() })
                .pipe(
                    retry({
                        count: 2,
                        delay: (error) =>
                            error instanceof HttpErrorResponse && error.status >= 502
                                ? timer(2000)
                                : throwError(() => error),
                    }),
                    catchError((error) => of({ _error: true, message: error.message }))
                );
        }

        const body = {
            authorizationToken: `Bearer ${getAuthToken()}`,
            requestType: globalMessages.getDocumentsCategory,
            policyNumber,
            term: policyTerm ? policyTerm.padStart(2, '0') : policyTerm,
            dmsRole: [],
            ...(effectiveDate && { effectiveDate }),
        };
        return this.httpClient
            .post(this.config.policyOrchestratorUrl, body, {
                headers: this.orchestratorHeaders(),
            })
            .pipe(
                retry({
                    count: 2,
                    delay: (error) =>
                        error instanceof HttpErrorResponse && error.status >= 502
                            ? timer(2000)
                            : throwError(() => error),
                }),
                catchError((error) => of({ _error: true, message: error.message }))
            );
    }

    private fetchClaims(policyNumber: string, policyTerm?: string, effectiveDate?: string) {
        // Third-party flow: getClaimsForTP via the Orchestrator ({ policyNumber, effDt });
        // the term is derived server-side. No Bearer token; auth is the sessionId header.
        const claimsSessionId = this.sessionService.getSessionId();
        if (claimsSessionId) {
            const effDt =
                effectiveDate && effectiveDate !== 'unknown' ? this.formatEffDt(effectiveDate) : undefined;
            const tpBody = {
                requestType: 'getClaimsForTP',
                sessionId: claimsSessionId,
                policyNumber,
                ...(effDt && { effDt }),
            };
            return this.httpClient
                .post(this.config.policyOrchestratorUrl, tpBody, { headers: this.orchestratorHeaders() })
                .pipe(
                    retry({
                        count: 2,
                        delay: (error) =>
                            error instanceof HttpErrorResponse && error.status >= 502
                                ? timer(2000)
                                : throwError(() => error),
                    }),
                    catchError((error) => of({ _error: true, message: error.message }))
                );
        }

        const body = {
            authorizationToken: `Bearer ${getAuthToken()}`,
            requestType: globalMessages.getClaims,
            policyNumber,
            term: policyTerm,
            ...(effectiveDate && { effectiveDate }),
        };

        return this.httpClient
            .post(this.config.policyOrchestratorUrl, body, {
                headers: this.orchestratorHeaders(),
            })
            .pipe(
                retry({
                    count: 2,
                    delay: (error) =>
                        error instanceof HttpErrorResponse && error.status >= 502
                            ? timer(2000)
                            : throwError(() => error),
                }),
                catchError((error) => of({ _error: true, message: error.message }))
            );
    }

    loadPolicyData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PolicyActions.loadPolicyData),
            filter(({ effectiveDate }) => !!effectiveDate),
            switchMap(({ policyNumber, policyTerm, effectiveDate, expireDate }) => {
                return forkJoin([
                    this.fetchDCPolicy(policyNumber, effectiveDate, expireDate).pipe(delay(SIMULATED_DELAY_MS)),
                    this.fetchDCBilling(policyNumber, effectiveDate).pipe(delay(SIMULATED_DELAY_MS)),
                    this.fetchComm(policyNumber, policyTerm, effectiveDate).pipe(delay(SIMULATED_DELAY_MS)),
                    this.fetchDMS(policyNumber, policyTerm, effectiveDate).pipe(delay(SIMULATED_DELAY_MS)),
                    this.fetchClaims(policyNumber, policyTerm, effectiveDate).pipe(delay(SIMULATED_DELAY_MS)),
                ]).pipe(
                    mergeMap(
                        ([dcPolicyData, dcBillingData, commData, dmsData, claimsData]: [any, any, any, any, any]) => {
                            const syncActions: any[] = [
                                dcPolicyData?._error
                                    ? PolicyActions.loadDCPolicyFailure({ error: dcPolicyData.message })
                                    : PolicyActions.loadDCPolicySuccess({
                                          policyNumber,
                                          policySnapshot: dcPolicyData.body,
                                      }),
                                dcBillingData?._error
                                    ? PolicyActions.loadDCBillingFailure({ error: dcBillingData.message })
                                    : PolicyActions.loadDCBillingSuccess({
                                          policyNumber,
                                          billingSnapshot: dcBillingData.body,
                                      }),
                                commData?._error
                                    ? PolicyActions.loadCommFailure({ error: commData.message })
                                    : PolicyActions.loadCommSuccess({ policyNumber, inOutCommSnapshot: commData }),
                                dmsData?._error
                                    ? PolicyActions.loadDMSFailure({ error: dmsData.message })
                                    : PolicyActions.loadDMSSuccess({ policyNumber, dmsSnapshot: dmsData }),
                                claimsData?._error
                                    ? PolicyActions.loadClaimsFailure({ error: claimsData.message })
                                    : PolicyActions.loadClaimsSuccess({ policyNumber, claimsSnapshot: claimsData }),
                            ];

                            const policyInfo = !dcPolicyData?._error
                                ? dcPolicyData?.body?.policyGeneralInformation
                                : null;
                            const resolvedPolicyNumber = policyInfo?.policyNumber?.value ?? policyNumber;
                            const effDt: string = policyInfo?.effectiveDate?.value ?? '';
                            const expDt: string = policyInfo?.expirationDate?.value ?? '';

                            const payPlan$ =
                                effDt && expDt
                                    ? this.fetchPayPlan(resolvedPolicyNumber, effDt, expDt).pipe(
                                          map((payPlanData: any) =>
                                              payPlanData?._error
                                                  ? PolicyActions.loadPayPlanFailure({ error: payPlanData.message })
                                                  : PolicyActions.loadPayPlanSuccess({
                                                        policyNumber,
                                                        payPlanSnapshot: payPlanData.body,
                                                    })
                                          )
                                      )
                                    : EMPTY;

                            return merge(of(...syncActions), payPlan$);
                        }
                    )
                );
            })
        )
    );
    fetchPolicyTerms$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PolicyActions.loadPolicyData),
            // skip re-dispatched actions that already have effectiveDate set
            filter(({ effectiveDate }) => !effectiveDate),

            switchMap(({ policyNumber, policyTerm }) => {
                // Third-party flow: resolve terms via the Orchestrator
                // (getPolicyTermsForTP) so the UI does not call PhoneSearch
                // directly. The Orchestrator returns termData already mapped and
                // sorted latest-first. CSR keeps the direct PhoneSearch call.
                const isTP = !!this.sessionService.getSessionId();
                const terms$ = isTP
                    ? this.httpClient
                          .post<any>(
                              this.config.policyOrchestratorUrl,
                              { requestType: 'getPolicyTermsForTP', sessionId: this.sessionService.getSessionId(), policyNumber },
                              { headers: this.orchestratorHeaders() }
                          )
                          .pipe(map((response) => (response?.termData ?? []) as any[]))
                    : this.httpClient
                          .post<any>(
                              this.config.phoneSearchApi,
                              { requestType: 'policyLookup', payload: { policyNumber } },
                              {
                                  headers: new HttpHeaders({
                                      'Content-Type': 'application/json',
                                      'x-api-key': this.config.phoneSearchApiKey,
                                  }),
                              }
                          )
                          .pipe(
                              map((response) =>
                                  ((response?.policies ?? []) as any[])
                                      .filter((p) => p.term !== undefined && p.term !== null)
                                      .map((p) => ({
                                          term: String(p.term),
                                          startDate: p.start_date ?? '',
                                          endDate: p.end_date ?? '',
                                      }))
                                      .sort((a, b) => Number(b.term) - Number(a.term))
                              )
                          );

                return terms$.pipe(
                    switchMap((termData: { term: string; startDate: string; endDate: string }[]) => {
                        const matched = policyTerm
                            ? termData.find((t) => Number(t.term) === Number(policyTerm))
                            : null;
                        const resolved = matched ?? termData[0] ?? null;

                        return [
                            PolicyActions.storePolicyTerms({ terms: termData.map((t) => t.term), termData }),
                            PolicyActions.loadPolicyData({
                                policyNumber,
                                policyTerm: resolved?.term ?? policyTerm,
                                effectiveDate: resolved?.startDate ?? 'unknown',
                                expireDate: resolved?.endDate ?? 'unknown',
                            }),
                        ];
                    }),
                    catchError(() =>
                        of(
                            PolicyActions.storePolicyTerms({ terms: [], termData: [] }),
                            PolicyActions.loadPolicyData({
                                policyNumber,
                                policyTerm,
                                effectiveDate: 'unknown',
                                expireDate: 'unknown',
                            })
                        )
                    )
                );
            })
        )
    );

    refreshDCPolicy$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PolicyActions.refreshDCPolicy),
            switchMap(({ policyNumber, effectiveDate, expireDate }) =>
                this.fetchDCPolicy(policyNumber, effectiveDate, expireDate).pipe(
                    map((data: any) =>
                        data?._error
                            ? PolicyActions.loadDCPolicyFailure({ error: data.message })
                            : PolicyActions.loadDCPolicySuccess({ policyNumber, policySnapshot: data.body })
                    )
                )
            )
        )
    );

    retryDCPolicy$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PolicyActions.retryDCPolicy),
            switchMap(({ policyNumber, effectiveDate, expireDate }) =>
                this.fetchDCPolicy(policyNumber, effectiveDate, expireDate).pipe(
                    map((data: any) =>
                        data?._error
                            ? PolicyActions.loadDCPolicyFailure({ error: data.message })
                            : PolicyActions.loadDCPolicySuccess({ policyNumber, policySnapshot: data.body })
                    )
                )
            )
        )
    );

    retryDCBilling$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PolicyActions.retryDCBilling),
            switchMap(({ policyNumber }) =>
                this.fetchDCBilling(policyNumber).pipe(
                    map((data: any) =>
                        data?._error
                            ? PolicyActions.loadDCBillingFailure({ error: data.message })
                            : PolicyActions.loadDCBillingSuccess({ policyNumber, billingSnapshot: data.body })
                    )
                )
            )
        )
    );

    retryComm$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PolicyActions.retryComm),
            switchMap(({ policyNumber, policyTerm }) =>
                this.fetchComm(policyNumber, policyTerm).pipe(
                    map((data: any) =>
                        data?._error
                            ? PolicyActions.loadCommFailure({ error: data.message })
                            : PolicyActions.loadCommSuccess({ policyNumber, inOutCommSnapshot: data })
                    )
                )
            )
        )
    );

    retryDMS$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PolicyActions.retryDMS),
            switchMap(({ policyNumber, policyTerm }) =>
                this.fetchDMS(policyNumber, policyTerm).pipe(
                    map((data: any) =>
                        data?._error
                            ? PolicyActions.loadDMSFailure({ error: data.message })
                            : PolicyActions.loadDMSSuccess({ policyNumber, dmsSnapshot: data })
                    )
                )
            )
        )
    );

    retryClaims$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PolicyActions.retryClaims),
            switchMap(({ policyNumber, policyTerm }) =>
                this.fetchClaims(policyNumber, policyTerm).pipe(
                    map((data: any) =>
                        data?._error
                            ? PolicyActions.loadClaimsFailure({ error: data.message })
                            : PolicyActions.loadClaimsSuccess({ policyNumber, claimsSnapshot: data })
                    )
                )
            )
        )
    );

    retryPayPlan$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PolicyActions.retryPayPlan),
            switchMap(({ policyNumber, effDt, expDt }) =>
                this.fetchPayPlan(policyNumber, effDt, expDt).pipe(
                    map((data: any) =>
                        data?._error
                            ? PolicyActions.loadPayPlanFailure({ error: data.message })
                            : PolicyActions.loadPayPlanSuccess({ policyNumber, payPlanSnapshot: data.body })
                    )
                )
            )
        )
    );

    verifyContact$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PolicyActions.loadDCPolicySuccess),
            switchMap(({ policyNumber, policySnapshot }) => {
                const insuredPhone = policySnapshot?.contactPreferences?.insuredInformation?.phone?.value ?? '';
                const insuredEmail = policySnapshot?.contactPreferences?.insuredInformation?.email?.value ?? '';
                const producerPhone = policySnapshot?.agencyInformation?.phoneNumber?.value ?? '';
                const producerEmail = policySnapshot?.agencyInformation?.contactEmail?.value ?? '';
                const additionalInsureds = (policySnapshot?.contactPreferences?.additionalNamedInsured ?? []).map(
                    (ai: any) => ({
                        phone: ai?.phoneNumber?.value ?? '',
                        email: ai?.email?.value ?? '',
                    })
                );
                const additionalInterests = (policySnapshot?.contactPreferences?.additionalInterest ?? []).map(
                    (at: any) => ({
                        email: at?.email?.value ?? '',
                    })
                );
                const designatedThirdParties = (policySnapshot?.contactPreferences?.designatedThirdParty ?? []).map(
                    (dtp: any) => ({
                        email: dtp?.email?.value ?? '',
                    })
                );
                const escrows = (policySnapshot?.contactPreferences?.escrow ?? []).map(
                    (esc: any) => ({
                        email: esc?.email?.value ?? '',
                    })
                );
                const coOwners: { phone: string; email: string }[] = [];
                for (let i = 1; ; i++) {
                    const phone =
                        policySnapshot?.contactPreferences?.insuredInformation?.[`coOwner${i}Phone`]?.value ?? '';
                    const email =
                        policySnapshot?.contactPreferences?.insuredInformation?.[`coOwner${i}Email`]?.value ?? '';
                    if (!phone && !email) break;
                    coOwners.push({ phone, email });
                }
                if (
                    !insuredPhone &&
                    !insuredEmail &&
                    !producerPhone &&
                    !producerEmail &&
                    !additionalInsureds.length &&
                    !additionalInterests.length &&
                    !designatedThirdParties.length &&
                    !escrows.length &&
                    !coOwners.length
                )
                    return EMPTY;
                return this.verifyContactService
                    .verifyContacts(
                        insuredPhone,
                        insuredEmail,
                        coOwners,
                        producerPhone,
                        producerEmail,
                        additionalInsureds,
                        additionalInterests,
                        designatedThirdParties,
                        escrows
                    )
                    .pipe(
                        map(
                            ({
                                insuredPhoneValid,
                                insuredPhoneCellular,
                                insuredEmailValid,
                                producerPhoneValid,
                                producerPhoneCellular,
                                producerEmailValid,
                                additionalInsuredValidation,
                                additionalInterestValidation,
                                designatedThirdPartyValidation,
                                escrowValidation,
                                coOwnerValidation,
                            }) =>
                                PolicyActions.verifyContactSuccess({
                                    policyNumber,
                                    insuredPhoneValid,
                                    insuredPhoneCellular,
                                    insuredEmailValid,
                                    producerPhoneValid,
                                    producerPhoneCellular,
                                    producerEmailValid,
                                    additionalInsuredValidation,
                                    additionalInterestValidation,
                                    designatedThirdPartyValidation,
                                    escrowValidation,
                                    coOwnerValidation,
                                })
                        ),
                        catchError(() => EMPTY)
                    );
            })
        )
    );
}
